import React, { useCallback, useLayoutEffect, useState } from 'react';
import { StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { rem, Spacer, Button, AppView, CardView, SCROLL_INDICATOR_INSETS, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import RadioButton from 'design-system/RadioButton';
import SectionHeader from 'design-system/SectionHeader';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import ProductTransferDetails from 'features/invest/components/ProductTransferDetails';
import { useCreateProductTransfer } from 'features/invest/hooks/useCreateProductTransfer';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import Modal from 'utils/packages/Modal';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { isWeb } from '../../../constants';
const strings = {
    title: (t) => `Review and confirm ${t === 'JISA' ? 'Junior ISA' : 'ISA'} transfer`,
    subTitle: `Check the details and agree to the transfer declaration before confirming`,
    transferringFrom: 'Transferring from',
    transferDeclaration: 'Transfer declaration',
    iAgree: (t) => `I agree that you can manage my ${t === 'JISA' ? 'child’s Junior ISA' : 'ISA'} transfer. `, // space intentional
    agreeEarlyDisclosureFee: 'I understand my current provider may charge an ',
    earlyClosureFee: 'early closure fee',
    confirm: 'Confirm transfer',
};
const ProductTransferConfirmationScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const colors = useColors();
    const { accountId, provider, balance, signatureSvg, uploadInfo, transferTaxYear, type, ...identification } = route.params;
    const providerName = provider.shortName || provider.officialName;
    const accountType = accountId ? 'JISA' : 'ISA';
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const hasEarlyClosureFee = type === 'CASH' &&
        ((accountType === 'ISA' && provider.isaSupportsPayUk) || (accountType === 'JISA' && provider.jisaSupportsPayUk));
    const [isCreating, createProductTransfer] = useCreateProductTransfer(accountId);
    const [agreed, setAgreed] = useState(false);
    const [agreeEarlyClosureFee, setAgreeEarlyClosureFee] = useState(false);
    const handleConfirm = useCallback(() => {
        createProductTransfer({
            balance,
            provider,
            uploadInfo,
            signatureSvg,
            type: 'CASH',
            transferTaxYear,
            previousProviderAccountType: type,
            earlyClosureWaiver: agreeEarlyClosureFee,
            ...identification,
        });
    }, [
        createProductTransfer,
        type,
        balance,
        provider,
        uploadInfo,
        signatureSvg,
        transferTaxYear,
        agreeEarlyClosureFee,
        identification,
    ]);
    const openTransferDeclaration = useCallback(() => {
        navigation.navigate('ProductTransferDeclaration', { accountType });
    }, [accountType]);
    const showEarlyClosureFeeModal = useCallback(() => {
        Modal.showConfirmation({
            title: 'What’s an early closure fee?',
            subtitle: [
                'Your current provider may charge a fee when you transfer your money or assets out early. This isn’t charged by us.',
                'We’d recommend checking your current provider’s fee policy before continuing.',
            ],
            confirmButtonTitle: 'Okay',
        });
    }, []);
    return (<AppView withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <ScreenInfoHeader title={strings.title(accountType)} subTitle={strings.subTitle}/>
          <Spacer h={8}/>
          <SectionHeader horizontalMargins>{strings.transferringFrom}</SectionHeader>
          <CardView noMarginBottom>
            <ProductTransferDetails type={type} balance={balance} accountType={accountType} providerName={providerName} identification={identification} providerLogoUrl={provider.imgUrl ?? undefined}/>
          </CardView>
          <SectionHeader largeVerticalMargins horizontalMargins>
            {strings.transferDeclaration}
          </SectionHeader>
          <TouchableBounceCard onDark noMarginTop withPadding noMarginBottom onPress={() => setAgreed((old) => !old)} style={styles.transferDeclarationCard}>
            <RadioButton type="multiple" isChecked={agreed}/>
            <Text TextThin-14 flex>
              {strings.iAgree(accountType)}
              <Text Text-14 Primary onPress={openTransferDeclaration} suppressHighlighting>
                {strings.transferDeclaration}
              </Text>
            </Text>
          </TouchableBounceCard>

          {!!hasEarlyClosureFee && (<TouchableBounceCard onDark withPadding noMarginBottom onPress={() => setAgreeEarlyClosureFee((old) => !old)} style={styles.transferDeclarationCard}>
              <RadioButton type="multiple" isChecked={agreeEarlyClosureFee}/>
              <Text TextThin-14 flex>
                {strings.agreeEarlyDisclosureFee}
                <Text Text-14 Primary onPress={showEarlyClosureFeeModal} suppressHighlighting>
                  {strings.earlyClosureFee}
                </Text>
              </Text>
            </TouchableBounceCard>)}
        </Animated.ScrollView>
        <Button brand floating title={strings.confirm} onPress={handleConfirm} isFetching={isCreating} disabled={!agreed || (hasEarlyClosureFee && !agreeEarlyClosureFee)}/>
      </AppView>);
}, ({ colors }) => ({
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
export default ProductTransferConfirmationScreen;
const styles = StyleSheet.create({
    transferDeclarationCard: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(16),
    },
});
